/*
  Licensed under the Apache License, Version 2.0 (the "License"); you may not use
  this file except in compliance with the License. You may obtain a copy of the
  License at

      https://www.apache.org/licenses/LICENSE-2.0

  Unless required by applicable law or agreed to in writing, software distributed
  under the License is distributed on an "AS IS" BASIS, WITHOUT WARRANTIES OR
  CONDITIONS OF ANY KIND, either express or implied. See the License for the
  specific language governing permissions and limitations under the License.
*/

import yn from 'yn';

import {
  DEFAULT_PROJECTION,
  isValidProjection,
  LayerProviders,
  MapProjection,
  SUPER_ADMIN_WORKSPACE,
} from '@marapp/earth-shared';

const NODE_ENV: string = process.env.NODE_ENV;
const PUBLIC_URL: string = process.env.VITE_APP_PUBLIC_URL || '/';
const PUBLIC_ORG: string = process.env.VITE_APP_ADMIN_PUBLIC_ORG || 'UNBL';
const APP_NAME: string =
  process.env.VITE_APP_MAP_NAME || process.env.VITE_APP_NAME || 'UN Biodiversity Lab';
const PRIMARY_COLOR_LIGHT: string = process.env.VITE_APP_PRIMARY_COLOR_LIGHT;
const PRIMARY_COLOR_MAIN: string = process.env.VITE_APP_PRIMARY_COLOR_MAIN;
const PRIMARY_COLOR_DARK: string = process.env.VITE_APP_PRIMARY_COLOR_DARK;
const COMPANY_URL: string = process.env.VITE_APP_COMPANY_URL;
const COMPANY_ABOUT_URL: string = process.env.VITE_APP_COMPANY_ABOUT_URL;
const COMPANY_SUPPORT_URL: string = process.env.VITE_APP_COMPANY_SUPPORT_URL;
const POWERED_BY: string = process.env.VITE_APP_POWERED_BY || 'UNEP-WCMC';
const POWERED_BY_URL: string = process.env.VITE_APP_POWERED_BY_URL || 'https://www.unep-wcmc.org';
const POWERED_BY_PC_URL: string = process.env.VITE_APP_POWERED_BY_PC_URL;
const MAP_BASE_URL: string = process.env.VITE_APP_MAP_BASE_URL || '/';
const MAP_API_URL: string = process.env.VITE_APP_MAP_API_URL;
const MAP_CDN_URL: string = process.env.VITE_APP_MAP_CDN_URL;
const MAP_GRAYSCALE_STYLE: string = process.env.VITE_APP_MAP_GRAYSCALE_STYLE;
const MAP_SATELLITE_STYLE: string = process.env.VITE_APP_MAP_SATELLITE_STYLE;
const MAP_DARK_STYLE: string = process.env.VITE_APP_MAP_DARK_STYLE;
const MAP_TERMS_OF_USE_URL: string = process.env.VITE_APP_MAP_TERMS_OF_USE_URL;
const MAP_ADMIN_URL: string = process.env.VITE_APP_MAP_ADMIN_URL;
const MAP_APP_NAME: string = process.env.VITE_APP_MAP_NAME;
const MAP_MAPBOX_TOKEN: string = process.env.VITE_APP_MAP_MAPBOX_TOKEN;
const MAP_EXTERNAL_IDP_URL: string = process.env.VITE_APP_MAP_EXTERNAL_IDP_URL || '';
// Projection the map opens in, one of MAP_PROJECTIONS
const MAP_PROJECTION: MapProjection = isValidProjection(process.env.VITE_APP_MAP_PROJECTION)
  ? process.env.VITE_APP_MAP_PROJECTION
  : DEFAULT_PROJECTION;
const MAP_ENABLE_PUBLIC_ACCESS: boolean = yn(process.env.VITE_APP_MAP_ENABLE_PUBLIC_ACCESS, {
  default: false,
});
const MAP_SHOW_DISCLAIMER: boolean = yn(process.env.VITE_APP_MAP_SHOW_DISCLAIMER, {
  default: false,
});
const MAP_ENABLE_ELSA: boolean = yn(process.env.VITE_APP_MAP_ENABLE_ELSA, {
  default: false,
});

const MAP_SIDEBAR_WIDTH = 380;
const MAP_SIDEBAR_WIDTH_WIDE = 500;

// Legend panel widths. The narrow width is sized to visually peer with the
// sidebar; the wide width gives ELSA result tables enough room to render
// without horizontal scrolling.
const LEGEND_WIDTH = 380;
const LEGEND_WIDTH_WIDE = 590;

const STAC_PROVIDER_VALUE = LayerProviders.STAC;
const GEE_PROVIDER_VALUE = LayerProviders.GEE;
const UPLOAD_PROVIDER_VALUE = LayerProviders.UPLOAD_FILE;

/**
 * In case this is not passed in, we still want it to work.
 */
const ANALYTICS_CONSENT_COOKIE_NAME: string =
  process.env.VITE_APP_ANALYTICS_CONSENT_COOKIE_NAME || 'analytics-consent';
const ANALYTICS_CONSENT_COOKIE_SECURE: boolean = yn(
  process.env.VITE_APP_ANALYTICS_CONSENT_COOKIE_SECURE,
  { default: true } // must be set to false for local development over http://
);

const MIN_WORKSPACE_COUNT_TO_SHOW_TOGGLE_ALL: number = Number(
  process.env.VITE_APP_MAP_MIN_WORKSPACE_COUNT_TO_SHOW_TOGGLE_ALL || 5
);

const MAP_PUBLIC_ORG: string = process.env.VITE_APP_MAP_PUBLIC_ORG || 'UNBL';

const MAP_MIN_LAYER_SEARCH_KEY_LENGTH: number = Number(
  process.env.VITE_APP_MAP_MIN_LAYER_SEARCH_KEY_LENGTH || 3
);

// The GBIF API host, without a version: occurrence tiles are under `/v2`, the
// species and occurrence search under `/v1`.
const GBIF_API_URL: string = process.env.VITE_APP_GBIF_API_URL || 'https://api.gbif.org';

export {
  NODE_ENV,
  PUBLIC_URL,
  PUBLIC_ORG,
  ANALYTICS_CONSENT_COOKIE_NAME,
  ANALYTICS_CONSENT_COOKIE_SECURE,
  APP_NAME,
  COMPANY_URL,
  COMPANY_ABOUT_URL,
  COMPANY_SUPPORT_URL,
  POWERED_BY,
  POWERED_BY_URL,
  POWERED_BY_PC_URL,
  PRIMARY_COLOR_LIGHT,
  PRIMARY_COLOR_MAIN,
  PRIMARY_COLOR_DARK,
  MAP_BASE_URL,
  MAP_API_URL,
  MAP_CDN_URL,
  MAP_GRAYSCALE_STYLE,
  MAP_SATELLITE_STYLE,
  MAP_DARK_STYLE,
  MAP_SHOW_DISCLAIMER,
  MAP_TERMS_OF_USE_URL,
  MAP_ADMIN_URL,
  MAP_APP_NAME,
  MAP_MAPBOX_TOKEN,
  MAP_EXTERNAL_IDP_URL,
  MAP_PROJECTION,
  MAP_ENABLE_PUBLIC_ACCESS,
  MAP_ENABLE_ELSA,
  MAP_SIDEBAR_WIDTH,
  MAP_SIDEBAR_WIDTH_WIDE,
  LEGEND_WIDTH,
  LEGEND_WIDTH_WIDE,
  STAC_PROVIDER_VALUE,
  GEE_PROVIDER_VALUE,
  UPLOAD_PROVIDER_VALUE,
  SUPER_ADMIN_WORKSPACE,
  MIN_WORKSPACE_COUNT_TO_SHOW_TOGGLE_ALL,
  MAP_PUBLIC_ORG,
  MAP_MIN_LAYER_SEARCH_KEY_LENGTH,
  GBIF_API_URL,
};
